import { ReactNode } from "react";
import { Box, Flex } from "rebass/styled-components";
import { Swiper } from "swiper/react";
import { Mousewheel, Pagination, Navigation } from "swiper/modules";
import styled from "styled-components";

import { SectionTitle, StyledSwiperSlide } from "../styledComponents";
import { ContentItem } from "../ContentItem";
import { IContentItem } from "../@types";
import { Loading } from "../Loading";
import { Expandable } from "../../../Components/Expandable";
import { SuggestionBadge } from "../../../Components/SuggestionBadge";
import { Link } from "../../../Components/Link";

interface IContentFeed {
  data: IContentItem[];
  icon?: ReactNode;
  isMontageFeed?: boolean;
  loading?: boolean;
  title: string;
  /** Optional rich render of the title for the header only (title stays the plain-string identity). */
  titleNode?: ReactNode;
  isExpandable?: boolean;
  isProsFeed?: boolean;
  suggestionReason?: string;
  vanillaThumbnail?: boolean;
  titleTo?: string;
  featureBadge?: string;
  description?: string;
  linkLabel?: string;
}

const MAX_PER_ROW = 7;

const ContentFeed = ({
  data,
  icon,
  isMontageFeed,
  isExpandable,
  loading,
  title,
  titleNode,
  isProsFeed,
  suggestionReason,
  vanillaThumbnail,
  titleTo,
  featureBadge,
  description,
  linkLabel,
}: IContentFeed) => {
  if (loading) return <Loading />;
  if (!data || !data.length) return <></>;

  return (
    <Box style={{ position: "relative" }}>
      {!isExpandable && (
        <Box mb={4}>
          <Flex alignItems="center">
            <Box mt={2}>{icon}</Box>
            <SectionTitle>{titleNode ?? title}</SectionTitle>
            {featureBadge && <FeatureBadge>{featureBadge}</FeatureBadge>}
            {titleTo && (
              <Link to={titleTo}>
                <LearnMore>{linkLabel || "Learn More"}</LearnMore>
              </Link>
            )}
          </Flex>
          {description && (
            <FeatureDescription>{description}</FeatureDescription>
          )}
        </Box>
      )}
      <Box
        mb={6}
        style={{
          position: "relative",
          width: "100%",
        }}
      >
        <ContentFeedCarousel
          data={data.slice(0, MAX_PER_ROW)}
          title={title}
          isMontageFeed={isMontageFeed}
          isProsFeed={isProsFeed}
          suggestionReason={suggestionReason}
          vanillaThumbnail={vanillaThumbnail}
        />
      </Box>
      {data.length > MAX_PER_ROW && (
        <Expandable sectionName={title}>
          <Box pt={2}>
            <ContentFeedCarousel
              data={data.slice(MAX_PER_ROW, data.length)}
              isMontageFeed={isMontageFeed}
              title={title}
              suggestionReason={suggestionReason}
              vanillaThumbnail={vanillaThumbnail}
            />
          </Box>
        </Expandable>
      )}
    </Box>
  );
};

const ContentFeedCarousel = ({
  data,
  isMontageFeed,
  title,
  isProsFeed,
  suggestionReason,
  vanillaThumbnail,
}: IContentFeed) => {
  return (
    <Swiper
      centeredSlides={false}
      spaceBetween={16}
      slidesPerView={1}
      mousewheel={false}
      breakpoints={{
        640: {
          slidesPerView: 3,
        },
        979: {
          slidesPerView: 4,
        },
        1280: {
          slidesPerView: 5,
        },
        1600: {
          slidesPerView: 6,
        },
        2000: {
          slidesPerView: MAX_PER_ROW,
        },
      }}
      modules={[Mousewheel, Pagination, Navigation]}
    >
      {data.map((item: IContentItem, index: number) => (
        <StyledSwiperSlide key={index}>
          <ContentItemWrapper>
            {suggestionReason && (
              <SuggestionBadgeOverlay>
                <SuggestionBadge
                  reason={suggestionReason}
                  showTooltip={false}
                />
              </SuggestionBadgeOverlay>
            )}
            <ContentItem
              item={item}
              title={title}
              isMontage={isMontageFeed}
              isProsFeed={isProsFeed}
              vanillaThumbnail={vanillaThumbnail}
            />
          </ContentItemWrapper>
        </StyledSwiperSlide>
      ))}
    </Swiper>
  );
};

const LearnMore = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-left: 12px;
  text-transform: uppercase;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const ContentItemWrapper = styled(Box)`
  position: relative;
  width: 100%;
`;

const SuggestionBadgeOverlay = styled(Box)`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 15;

  @media (max-width: 480px) {
    top: 6px;
    left: 6px;
  }
`;

const FeatureBadge = styled.span`
  margin-left: 12px;
  padding: 3px 9px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.envyAlpha30};
  background: ${({ theme }) => theme.colors.envyAlpha10};
  color: ${({ theme }) => theme.colors.envy};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.envyAlpha05};
  white-space: nowrap;
`;

const FeatureDescription = styled.p`
  margin: 6px 0 0;
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  line-height: 1.5;
`;

export { ContentFeed };
